<script setup>
import MarketMetricCard from 'components/market/MarketMetricCard.vue';
import MarketSalaryStrip from 'components/market/MarketSalaryStrip.vue';
import { formatMarketCount, formatMarketMetric } from 'src/utils/market-util';
import { computed } from 'vue';

const $props = defineProps({
    snapshot: {
        type: Object,
        default: null
    }
});

const activeRoleCount = computed(() => {
    return Number.isFinite($props.snapshot?.jobCount) ? formatMarketMetric($props.snapshot.jobCount) : null;
});

const salary = computed(() => $props.snapshot?.salaries || $props.snapshot?.salary || null);

const advertisedMedian = computed(() => {
    if (salary.value?.isSampleSufficient === false || !Number.isFinite(salary.value?.median)) {
        return null;
    }
    return formatMarketMetric(salary.value.median, {
        type: 'salary',
        currency: salary.value.currency || 'AUD'
    });
});

const salaryContext = computed(() => {
    if (!salary.value) {
        return 'Advertised pay data has not been measured for this snapshot.';
    }
    if (advertisedMedian.value === null) {
        const sampleSize = salary.value.sampleSize || 0;
        return `${formatMarketCount(sampleSize, 'role')} with advertised pay ${sampleSize === 1 ? 'was' : 'were'} observed.`;
    }
    return `Annualised to ${salary.value.currency || 'AUD'} from ${formatMarketMetric(salary.value.sampleSize)} advertised salaries.`;
});

const salaryUnavailableMessage = computed(() => {
    if (!salary.value) {
        return 'Salary data unavailable';
    }
    return `Insufficient salary sample (${formatMarketMetric(salary.value.sampleSize || 0)} observed)`;
});

// Xprt only ingests an advertisement that states its pay, so the share of listings carrying pay is always 100% and
// measures nothing. The selection is named here instead, where it reads as how the inventory is chosen rather than
// as a finding about the Australian market.
const paySelectionNote = 'Xprt Dev tracks only roles that advertise pay, so every listing counted here states a salary.';
</script>

<template>
    <div class="market-readout readout-frame q-pa-xl">
        <div class="metric-band">
            <MarketMetricCard
                :note="1"
                :value="activeRoleCount"
                context="Distinct active listings in this market."
                label="Active roles"
                unavailable-message="Active role count unavailable"
            />
            <MarketMetricCard
                :context="salaryContext"
                :note="2"
                :unavailable-message="salaryUnavailableMessage"
                :value="advertisedMedian"
                label="Advertised median salary"
            />
        </div>
        <p class="font-mono text-caption text-hint q-mt-lg q-mb-none" data-test="pay-selection-note">
            {{ paySelectionNote }}
        </p>
        <MarketSalaryStrip :salary="salary" class="q-mt-xl"/>
    </div>
</template>

<style lang="scss" scoped>
.market-readout {
    .metric-band {
        display: grid;
        gap: 24px 48px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    // The frame's 48px padding leaves too narrow a column for the chart on a phone.
    @media (max-width: $breakpoint-xs-max) {
        padding: 24px;

        .metric-band {
            grid-template-columns: 1fr;
        }
    }
}
</style>
